// Real-time bridge for the in-app notifications list.
//
// Without this hook, the bell + desktop notifications relied on the
// React Query refetchInterval (60s) — so agents could be staring at
// a 0-badge bell while a P1 reply sat unread in the DB for almost a
// minute. With it, every INSERT on `notifications` arrives over the
// Supabase Realtime channel within ~200ms and we invalidate the
// query keys so the bell + desktop hook see the new row immediately.
//
// Requires migration 054 to be applied (adds `notifications` to the
// `supabase_realtime` publication). Without that migration, the
// subscription connects fine but never receives events.
//
// Mounted ONCE at AuthedLayout level (`App.tsx`) — same reasoning
// as `useDesktopNotifications` and `useTabAttention`. Subscription
// lifetime tracks the SPA's authenticated session; signing out
// removes the channel via the effect cleanup.
//
// RLS handles per-user filtering: the Realtime service evaluates
// the table's SELECT policies against the subscriber's JWT, so a
// user only receives events for rows where
// `recipient_slack_id = their slack_user_id`. The hook still applies
// a client-side filter on the slack id as a belt-and-suspenders so
// noisy edge cases (e.g. a service-role insert during admin tooling)
// don't trigger spurious invalidations.

import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/lib/auth-guard';
import { NOTIFICATIONS_MUTATION_KEY, invalidateNotifications } from '@/lib/queries';

export function useNotificationsRealtime(): void {
  const { teamMember } = useAuth();
  const qc = useQueryClient();
  const slackId = teamMember?.slack_user_id ?? null;

  useEffect(() => {
    if (!slackId) return;

    // Channel name uses the slack id so each tab gets its own
    // subscription. Supabase de-dupes the underlying socket so this
    // doesn't cost a new connection per tab.
    const channel = supabase
      .channel(`notifications:${slackId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          // RLS already filters server-side, but a Realtime filter
          // also reduces the wire traffic — we never even receive
          // events for rows we couldn't see.
          filter: `recipient_slack_id=eq.${slackId}`,
        },
        () => {
          // Invalidate every notification key so the bell list, tab
          // counts and unread badge all refresh. React Query handles the
          // de-dup of overlapping refetches if a burst of rows arrives.
          void invalidateNotifications(qc);
        },
      )
      // Also invalidate on UPDATE — covers the case where a teammate
      // (or the user on another device) marks a notification read
      // and we want the bell badge to drop without waiting for the
      // next poll. INSERT is the primary path; UPDATE is cheap
      // because the only column ever updated is `read_at`.
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'notifications',
          filter: `recipient_slack_id=eq.${slackId}`,
        },
        () => {
          // Our own read/unread click comes back as an UPDATE event. The
          // mutation already patched the cache optimistically and refetches
          // when it settles, so refetching here as well would flash the row
          // back to its old state for a round trip. A concurrent change from
          // another device is still picked up by that same settle refetch.
          // INSERT events are never skipped — a new row is always news.
          if (qc.isMutating({ mutationKey: NOTIFICATIONS_MUTATION_KEY }) > 0) return;
          void invalidateNotifications(qc);
        },
      )
      .subscribe((status, err) => {
        // Log subscription state so a broken channel is debuggable in
        // DevTools without instrumenting from scratch. Successful
        // `SUBSCRIBED` is the only happy state; CHANNEL_ERROR /
        // TIMED_OUT / CLOSED indicate the JWT didn't propagate or the
        // table isn't in the `supabase_realtime` publication
        // (migration 054 must be applied).
        if (status !== 'SUBSCRIBED') {
          // eslint-disable-next-line no-console
          console.warn('[notifications-realtime] subscribe status', status, err);
        }
      });

    // Belt-and-suspenders: an explicit invalidation when the tab
    // returns to visible. Browsers throttle background tabs' timers
    // (setIntervals down to once per minute on Chrome inactive tabs)
    // and occasionally let WebSockets go stale without notifying the
    // app. If we miss any events during a long background period,
    // this catches us up the instant the user looks at the tab again
    // — bell + unread count refresh, and the desktop hook sees the
    // delta inside the same render cycle.
    function onVisibility() {
      if (!document.hidden) void invalidateNotifications(qc);
    }
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      void supabase.removeChannel(channel);
    };
  }, [slackId, qc]);
}
